import { memo } from 'react';
import {
  X,
  AlignLeft,
  AlignCenter,
  AlignRight,
  LayoutList,
  Minus,
  Plus,
} from 'lucide-react';
import type {
  WhiteboardElement,
  DeviceFrame,
  FlowArrow,
  ElementState,
  ElementProps,
} from '../../types/whiteboard';
import { EditableList } from './EditableList';

interface Props {
  element: WhiteboardElement | null;
  frame: DeviceFrame | null;
  arrow: FlowArrow | null;
  frames: DeviceFrame[];
  onUpdateElement: (updates: Partial<WhiteboardElement>) => void;
  onUpdateFrame: (updates: Partial<DeviceFrame>) => void;
  onUpdateArrow: (updates: Partial<FlowArrow>) => void;
  onAutoArrange: () => void;
  onClose: () => void;
}

function NumberInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-xs text-muted w-8 flex-shrink-0">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
        step={step}
        className="flex-1 bg-surface-2 border border-default rounded-md px-2 py-1 text-xs text-default outline-none focus:border-primary min-w-0"
      />
    </div>
  );
}

function SegmentedControl({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string | React.ReactNode }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-muted block mb-1.5">{label}</label>
      <div className="flex bg-surface-2 rounded-md border border-default p-0.5">
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`
              flex-1 text-xs py-1 px-2 rounded transition-colors
              ${value === opt.value
                ? 'bg-surface text-primary font-medium shadow-sm'
                : 'text-muted hover:text-default'
              }
            `}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ToggleField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between cursor-pointer group">
      <span className="text-xs text-default">{label}</span>
      <div
        onClick={() => onChange(!checked)}
        className={`w-8 h-5 rounded-full relative transition-colors cursor-pointer
          ${checked ? 'bg-primary' : 'bg-surface-3'}
        `}
      >
        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform
          ${checked ? 'translate-x-3.5' : 'translate-x-0.5'}
        `} />
      </div>
    </label>
  );
}

function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-xs text-muted flex-1">{label}</label>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded border border-default cursor-pointer"
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-20 bg-surface-2 border border-default rounded px-1.5 py-0.5 text-[10px] text-default outline-none"
          placeholder="#RRGGBB"
        />
      </div>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted mb-2 mt-4 first:mt-0">
      {title}
    </h4>
  );
}

export const PropertiesPanel = memo(function PropertiesPanel({
  element,
  frame,
  arrow,
  frames: _frames,
  onUpdateElement,
  onUpdateFrame,
  onUpdateArrow,
  onAutoArrange,
  onClose,
}: Props) {
  const updateProps = (propUpdates: Partial<ElementProps>) => {
    if (!element) return;
    onUpdateElement({ props: { ...element.props, ...propUpdates } });
  };

  const props = element?.props ?? ({} as ElementProps);
  const type = element?.type;

  return (
    <aside className="w-72 bg-surface border-l border-default flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-default shrink-0">
        <h3 className="text-sm font-semibold text-default">
          {element ? element.label : frame ? frame.name : arrow ? 'Arrow' : 'Properties'}
        </h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-surface-2 text-muted hover:text-default">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 scrollbar-thin">
        {/* ================================================================ */}
        {/* ELEMENT PROPERTIES */}
        {/* ================================================================ */}
        {element && (
          <>
            {/* Position & Size */}
            <SectionHeader title="Position & Size" />
            <div className="grid grid-cols-2 gap-2">
              <NumberInput label="X" value={Math.round(element.relativeX ?? element.x)} onChange={(v) => onUpdateElement({ x: v, relativeX: element.parentFrameId ? v : undefined })} />
              <NumberInput label="Y" value={Math.round(element.relativeY ?? element.y)} onChange={(v) => onUpdateElement({ y: v, relativeY: element.parentFrameId ? v : undefined })} />
              <NumberInput label="W" value={Math.round(element.width)} onChange={(v) => onUpdateElement({ width: Math.max(20, v) })} min={20} />
              <NumberInput label="H" value={Math.round(element.height)} onChange={(v) => onUpdateElement({ height: Math.max(12, v) })} min={12} />
            </div>

            {/* State */}
            <SectionHeader title="State" />
            <div>
              <select
                value={element.state}
                onChange={(e) => onUpdateElement({ state: e.target.value as ElementState })}
                className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs text-default outline-none focus:border-primary"
              >
                <option value="default">Default</option>
                <option value="hover">Hover</option>
                <option value="focus">Focus</option>
                <option value="disabled">Disabled</option>
                <option value="error">Error</option>
                <option value="loading">Loading</option>
                <option value="filled">Filled</option>
              </select>
            </div>

            {/* ============ Type-specific sections ============ */}

            {/* BUTTON */}
            {type === 'button' && (
              <>
                <SectionHeader title="Button" />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Label</label>
                  <input
                    type="text"
                    value={props.buttonLabel || ''}
                    onChange={(e) => updateProps({ buttonLabel: e.target.value })}
                    className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary"
                  />
                </div>
                <SegmentedControl
                  label="Variant"
                  options={[
                    { value: 'primary', label: 'Primary' },
                    { value: 'secondary', label: 'Secondary' },
                    { value: 'ghost', label: 'Ghost' },
                    { value: 'danger', label: 'Danger' },
                  ]}
                  value={props.buttonVariant || 'primary'}
                  onChange={(v) => updateProps({ buttonVariant: v as any })}
                />
                <SegmentedControl
                  label="Size"
                  options={[
                    { value: 'sm', label: 'S' },
                    { value: 'md', label: 'M' },
                    { value: 'lg', label: 'L' },
                  ]}
                  value={props.buttonSize || 'md'}
                  onChange={(v) => updateProps({ buttonSize: v as any })}
                />
              </>
            )}

            {/* INPUT */}
            {type === 'input' && (
              <>
                <SectionHeader title="Input" />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Label</label>
                  <input type="text" value={props.inputLabel || ''} onChange={(e) => updateProps({ inputLabel: e.target.value })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Placeholder</label>
                  <input type="text" value={props.placeholder || ''} onChange={(e) => updateProps({ placeholder: e.target.value })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Type</label>
                  <select value={props.inputType || 'text'} onChange={(e) => updateProps({ inputType: e.target.value as any })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary">
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="password">Password</option>
                    <option value="number">Number</option>
                    <option value="search">Search</option>
                    <option value="tel">Tel</option>
                    <option value="url">URL</option>
                  </select>
                </div>
                <ToggleField label="Required" checked={props.required ?? false} onChange={(v) => updateProps({ required: v })} />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Helper Text</label>
                  <input type="text" value={props.helperText || ''} onChange={(e) => updateProps({ helperText: e.target.value })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary" placeholder="Optional help text" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Error Message</label>
                  <input type="text" value={props.errorMessage || ''} onChange={(e) => updateProps({ errorMessage: e.target.value })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary" placeholder="Error message (state=error)" />
                </div>
              </>
            )}

            {/* DROPDOWN */}
            {type === 'dropdown' && (
              <>
                <SectionHeader title="Dropdown" />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Placeholder</label>
                  <input type="text" value={props.dropdownPlaceholder || ''} onChange={(e) => updateProps({ dropdownPlaceholder: e.target.value })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary" />
                </div>
                <EditableList label="Options" items={props.dropdownOptions || []} onChange={(items) => updateProps({ dropdownOptions: items })} placeholder="Option" />
                <ToggleField label="Multi-select" checked={props.dropdownMultiple ?? false} onChange={(v) => updateProps({ dropdownMultiple: v })} />
                <ToggleField label="Searchable" checked={props.dropdownSearchable ?? false} onChange={(v) => updateProps({ dropdownSearchable: v })} />
              </>
            )}

            {/* CHECKBOX */}
            {type === 'checkbox' && (
              <>
                <SectionHeader title="Checkbox" />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Label</label>
                  <input type="text" value={props.checkboxLabel || ''} onChange={(e) => updateProps({ checkboxLabel: e.target.value })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary" />
                </div>
                <ToggleField label="Default Checked" checked={props.checked ?? false} onChange={(v) => updateProps({ checked: v })} />
              </>
            )}

            {/* TOGGLE */}
            {type === 'toggle' && (
              <>
                <SectionHeader title="Toggle" />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Label</label>
                  <input type="text" value={props.checkboxLabel || ''} onChange={(e) => updateProps({ checkboxLabel: e.target.value })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary" />
                </div>
                <ToggleField label="Default On" checked={props.checked ?? false} onChange={(v) => updateProps({ checked: v })} />
              </>
            )}

            {/* RADIO GROUP */}
            {type === 'radio_group' && (
              <>
                <SectionHeader title="Radio Group" />
                <EditableList label="Options" items={props.radioOptions || []} onChange={(items) => updateProps({ radioOptions: items })} placeholder="Option" />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Default Selected</label>
                  <select value={props.radioSelected ?? 0} onChange={(e) => updateProps({ radioSelected: Number(e.target.value) })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary">
                    {(props.radioOptions || []).map((opt, i) => (
                      <option key={i} value={i}>{opt}</option>
                    ))}
                  </select>
                </div>
                <SegmentedControl
                  label="Orientation"
                  options={[
                    { value: 'vertical', label: 'Vertical' },
                    { value: 'horizontal', label: 'Horizontal' },
                  ]}
                  value={props.radioOrientation || 'vertical'}
                  onChange={(v) => updateProps({ radioOrientation: v as any })}
                />
              </>
            )}

            {/* TABLE */}
            {type === 'table' && (
              <>
                <SectionHeader title="Table" />
                <EditableList label="Columns" items={props.tableColumns || []} onChange={(items) => updateProps({ tableColumns: items })} placeholder="Column" />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-muted flex-1">Rows</label>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateProps({ tableRows: Math.max(1, (props.tableRows || 3) - 1) })} className="p-0.5 rounded hover:bg-surface-2 text-muted"><Minus className="w-3 h-3" /></button>
                    <span className="text-xs text-default w-8 text-center">{props.tableRows || 3}</span>
                    <button onClick={() => updateProps({ tableRows: (props.tableRows || 3) + 1 })} className="p-0.5 rounded hover:bg-surface-2 text-muted"><Plus className="w-3 h-3" /></button>
                  </div>
                </div>
                <ToggleField label="Header Row" checked={props.tableHasHeader ?? true} onChange={(v) => updateProps({ tableHasHeader: v })} />
                <ToggleField label="Sortable" checked={props.tableSortable ?? false} onChange={(v) => updateProps({ tableSortable: v })} />
                <ToggleField label="Paginated" checked={props.tablePaginated ?? false} onChange={(v) => updateProps({ tablePaginated: v })} />
              </>
            )}

            {/* TEXT */}
            {type === 'text' && (
              <>
                <SectionHeader title="Text" />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Content</label>
                  <textarea value={props.textContent || ''} onChange={(e) => updateProps({ textContent: e.target.value })} rows={3} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary resize-none" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Style</label>
                  <select value={props.textStyle || 'body'} onChange={(e) => updateProps({ textStyle: e.target.value as any })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary">
                    <option value="h1">Heading 1</option>
                    <option value="h2">Heading 2</option>
                    <option value="h3">Heading 3</option>
                    <option value="h4">Heading 4</option>
                    <option value="body">Body</option>
                    <option value="caption">Caption</option>
                    <option value="label">Label</option>
                  </select>
                </div>
                <SegmentedControl
                  label="Alignment"
                  options={[
                    { value: 'left', label: <AlignLeft className="w-3.5 h-3.5" /> },
                    { value: 'center', label: <AlignCenter className="w-3.5 h-3.5" /> },
                    { value: 'right', label: <AlignRight className="w-3.5 h-3.5" /> },
                  ]}
                  value={props.textAlign || 'left'}
                  onChange={(v) => updateProps({ textAlign: v as any })}
                />
                <ColorInput label="Color" value={props.textColor || ''} onChange={(v) => updateProps({ textColor: v })} />
              </>
            )}

            {/* IMAGE */}
            {type === 'image' && (
              <>
                <SectionHeader title="Image" />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Source URL</label>
                  <input type="text" value={props.imageSrc || ''} onChange={(e) => updateProps({ imageSrc: e.target.value })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary" placeholder="https://..." />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Alt Text</label>
                  <input type="text" value={props.imageAlt || ''} onChange={(e) => updateProps({ imageAlt: e.target.value })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary" />
                </div>
                <SegmentedControl
                  label="Fit"
                  options={[
                    { value: 'cover', label: 'Cover' },
                    { value: 'contain', label: 'Contain' },
                    { value: 'fill', label: 'Fill' },
                  ]}
                  value={props.imageObjectFit || 'cover'}
                  onChange={(v) => updateProps({ imageObjectFit: v as any })}
                />
                <NumberInput label="Radius" value={props.imageRadius ?? 8} onChange={(v) => updateProps({ imageRadius: v })} min={0} max={100} />
              </>
            )}

            {/* SHAPES */}
            {(type === 'shape_rect' || type === 'shape_circle') && (
              <>
                <SectionHeader title="Shape" />
                <ColorInput label="Fill" value={props.shapeFill || '#E2E6F3'} onChange={(v) => updateProps({ shapeFill: v })} />
                <ColorInput label="Stroke" value={props.shapeStroke || '#C9D1EA'} onChange={(v) => updateProps({ shapeStroke: v })} />
                <NumberInput label="Stroke W" value={props.shapeStrokeWidth ?? 1} onChange={(v) => updateProps({ shapeStrokeWidth: v })} min={0} max={8} />
                {type === 'shape_rect' && (
                  <NumberInput label="Radius" value={props.shapeCornerRadius ?? 8} onChange={(v) => updateProps({ shapeCornerRadius: v })} min={0} max={100} />
                )}
                <NumberInput label="Opacity" value={props.shapeOpacity ?? 100} onChange={(v) => updateProps({ shapeOpacity: v })} min={0} max={100} />
              </>
            )}

            {/* CONTAINER / CARD / MODAL */}
            {(type === 'container' || type === 'card' || type === 'modal') && (
              <>
                <SectionHeader title="Container" />
                <ColorInput label="Background" value={props.containerBackground || 'transparent'} onChange={(v) => updateProps({ containerBackground: v })} />
                <NumberInput label="Padding" value={props.containerPadding ?? 16} onChange={(v) => updateProps({ containerPadding: v })} min={0} max={64} />
                <NumberInput label="Radius" value={props.containerBorderRadius ?? 8} onChange={(v) => updateProps({ containerBorderRadius: v })} min={0} max={50} />
                <ToggleField label="Border" checked={props.containerBorder ?? false} onChange={(v) => updateProps({ containerBorder: v })} />
                {props.containerBorder && (
                  <ColorInput label="Border Color" value={props.containerBorderColor || ''} onChange={(v) => updateProps({ containerBorderColor: v })} />
                )}
                <ToggleField label="Shadow" checked={props.containerShadow ?? false} onChange={(v) => updateProps({ containerShadow: v })} />
                {props.containerShadow && (
                  <SegmentedControl
                    label="Shadow Size"
                    options={[
                      { value: 'sm', label: 'S' },
                      { value: 'md', label: 'M' },
                      { value: 'lg', label: 'L' },
                    ]}
                    value={props.containerShadowSize || 'md'}
                    onChange={(v) => updateProps({ containerShadowSize: v as any })}
                  />
                )}
              </>
            )}

            {/* TABS */}
            {type === 'tabs' && (
              <>
                <SectionHeader title="Tabs" />
                <EditableList label="Tab Items" items={props.dropdownOptions || []} onChange={(items) => updateProps({ dropdownOptions: items })} placeholder="Tab" />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Active Tab</label>
                  <select value={props.radioSelected ?? 0} onChange={(e) => updateProps({ radioSelected: Number(e.target.value) })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary">
                    {(props.dropdownOptions || []).map((opt, i) => (
                      <option key={i} value={i}>{opt}</option>
                    ))}
                  </select>
                </div>
                <SegmentedControl
                  label="Style"
                  options={[
                    { value: 'underline', label: 'Underline' },
                    { value: 'pills', label: 'Pills' },
                    { value: 'boxed', label: 'Boxed' },
                  ]}
                  value={props.tabStyle || 'underline'}
                  onChange={(v) => updateProps({ tabStyle: v as any })}
                />
              </>
            )}

            {/* ACCORDION */}
            {type === 'accordion' && (
              <>
                <SectionHeader title="Accordion" />
                <EditableList label="Sections" items={props.dropdownOptions || []} onChange={(items) => updateProps({ dropdownOptions: items })} placeholder="Section" />
                <ToggleField label="Allow Multiple Open" checked={props.accordionAllowMultiple ?? false} onChange={(v) => updateProps({ accordionAllowMultiple: v })} />
              </>
            )}

            {/* LIST */}
            {type === 'list' && (
              <>
                <SectionHeader title="List" />
                <EditableList label="Items" items={props.dropdownOptions || []} onChange={(items) => updateProps({ dropdownOptions: items })} placeholder="Item" />
                <ToggleField label="Dividers" checked={props.listDividers ?? true} onChange={(v) => updateProps({ listDividers: v })} />
                <ToggleField label="Show Icon" checked={props.listShowIcon ?? false} onChange={(v) => updateProps({ listShowIcon: v })} />
              </>
            )}

            {/* ICON */}
            {type === 'icon' && (
              <>
                <SectionHeader title="Icon" />
                <div>
                  <label className="text-xs font-medium text-muted block mb-1">Symbol / Emoji</label>
                  <input type="text" value={props.textContent || ''} onChange={(e) => updateProps({ textContent: e.target.value })} className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-sm outline-none focus:border-primary text-center" maxLength={4} />
                </div>
              </>
            )}

            {/* Label */}
            <SectionHeader title="Label" />
            <div>
              <input
                type="text"
                value={element.label}
                onChange={(e) => onUpdateElement({ label: e.target.value })}
                className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary"
                placeholder="Element label"
              />
            </div>

            {/* BA Notes */}
            <SectionHeader title="BA Notes" />
            <div>
              <textarea
                value={props.notes || ''}
                onChange={(e) => updateProps({ notes: e.target.value })}
                rows={3}
                className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary resize-none"
                placeholder="Add notes..."
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted block mb-1">Tags</label>
              <input
                type="text"
                value={(props.tags || []).join(', ')}
                onChange={(e) => updateProps({ tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary"
                placeholder="tag1, tag2, ..."
              />
            </div>
          </>
        )}

        {/* ================================================================ */}
        {/* FRAME PROPERTIES */}
        {/* ================================================================ */}
        {frame && (
          <>
            <SectionHeader title="Frame" />
            <div>
              <label className="text-xs font-medium text-muted block mb-1">Name</label>
              <input
                type="text"
                value={frame.name}
                onChange={(e) => onUpdateFrame({ name: e.target.value })}
                className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary"
              />
            </div>

            <SectionHeader title="Position & Size" />
            <div className="grid grid-cols-2 gap-2">
              <NumberInput label="X" value={Math.round(frame.x)} onChange={(v) => onUpdateFrame({ x: v })} />
              <NumberInput label="Y" value={Math.round(frame.y)} onChange={(v) => onUpdateFrame({ y: v })} />
              <NumberInput label="W" value={frame.viewportWidth} onChange={(v) => onUpdateFrame({ viewportWidth: Math.max(200, v) })} min={200} />
              <NumberInput label="H" value={frame.viewportHeight} onChange={(v) => onUpdateFrame({ viewportHeight: Math.max(200, v) })} min={200} />
            </div>

            <SectionHeader title="Appearance" />
            <ColorInput label="Background" value={frame.backgroundColor} onChange={(v) => onUpdateFrame({ backgroundColor: v })} />

            <SectionHeader title="Layout" />
            <div>
              <label className="text-xs text-muted mb-1 block">Children: {frame.children.length} elements</label>
              <button
                onClick={onAutoArrange}
                className="w-full btn btn-secondary text-xs py-1.5 flex items-center gap-1.5 justify-center"
              >
                <LayoutList className="w-3.5 h-3.5" />
                Auto-arrange
              </button>
            </div>
          </>
        )}

        {/* ================================================================ */}
        {/* ARROW PROPERTIES */}
        {/* ================================================================ */}
        {arrow && (
          <>
            <SectionHeader title="Arrow" />
            <div>
              <label className="text-xs font-medium text-muted block mb-1">Label</label>
              <input
                type="text"
                value={arrow.label || ''}
                onChange={(e) => onUpdateArrow({ label: e.target.value })}
                className="w-full bg-surface-2 border border-default rounded-md px-2 py-1.5 text-xs outline-none focus:border-primary"
                placeholder="e.g. On Submit"
              />
            </div>
            <SegmentedControl
              label="Curve Type"
              options={[
                { value: 'bezier', label: 'Bezier' },
                { value: 'orthogonal', label: 'Ortho' },
                { value: 'straight', label: 'Straight' },
              ]}
              value={arrow.curveType}
              onChange={(v) => onUpdateArrow({ curveType: v as any })}
            />
            <SegmentedControl
              label="Line Style"
              options={[
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
              ]}
              value={arrow.style}
              onChange={(v) => onUpdateArrow({ style: v as any })}
            />
            <ColorInput label="Color" value={arrow.color} onChange={(v) => onUpdateArrow({ color: v })} />
          </>
        )}
      </div>
    </aside>
  );
});
